import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, ref, watch } from "vue";
import store from "@/state/store";
import { roleOptions } from "@/composables/profileUtils";
import {
  KCMPPFieldType,
  KCMPPValuesTable,
  KCMPPProfile,
} from "@/types/kcm-profile-api.types";

import PhotoUpload from "@/components/profile/photo-upload.vue";
import { extractColorsFromSrc } from "extract-colors";
import { FinalColor } from "extract-colors/lib/types/Color";
import { hexToRgb } from "@/composables/colorMath";
import Multiselect from "vue-multiselect";
import { setPersonalization } from "./useSetup";
import { personalizationOnboardingHeader } from "./usePersonalizationOnboarding";


export default /*#__PURE__*/_defineComponent({
  __name: 'personalization-onboarding-modal',
  emits: ["exit", "save"],
  setup(__props, { emit }: { emit: ({ (e: "exit"): void; (e: "save"): void }), expose: any, slots: any, attrs: any }) {



/**
 * Profile state
 */
const activeImg = ref("");

const loadedProfile = computed(() =>
  store.getters["profile/getProfileByFeature"]("basic")
);

const photo = ref(loadedProfile.value?.photo?.value ?? "");
const logo = ref(loadedProfile.value?.logo?.value ?? "");
const logoPalette = ref<string[]>([]);
const company = ref(loadedProfile.value?.company?.value ?? "");
const jobTitle = ref(loadedProfile.value?.job_title?.value ?? "");
const brandColor = ref(
  "#" +
    (loadedProfile.value?.primary_color?.value ??
      (process.env.VUE_APP_INDUSTRY_BRAND_COLOR || "#00aeef"))
);

const role = ref(
  roleOptions.find((role) => {
    return role.value === loadedProfile.value?.primary_role?.value;
  }) || roleOptions[0]
);

/**
 * Photo/Logo upload logic
 */
function setUploader(type: string): void {
  activeImg.value = type;
}

function imageUploaded(url: string): void {
  const cleanedUrl = url.replace(/^https?:/, "");
  if (activeImg.value === "photo") photo.value = cleanedUrl;
  if (activeImg.value === "logo") logo.value = cleanedUrl;
  activeImg.value = "";
}

watch(logo, async (value: string): Promise<void> => {
  try {
    const palette = await extractColorsFromSrc(`https:${value}`, {
      pixels: 64000,
      distance: 0.33,
      crossOrigin: "anonymous",
    });
    logoPalette.value = palette.map((color: FinalColor) => color.hex);
  } catch (err) {
    console.error("Error extracting logo colors:", err);
  }
});

/**
 * Brand color helpers
 */
const contrastColor = computed(() => {
  const rgb = hexToRgb(brandColor.value);
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightness > 170 ? "black" : "white";
});

const badColorClass = computed((): string => {
  return contrastColor.value === "white" ? "d-none" : "text-danger fw-bold";
});

/**
 * Profile update logic
 */
const saving = ref(false);
const saveError = ref(false);
const missingRole = ref(false);

const firstName = computed(() => {
  return (
    loadedProfile.value?.first_name?.value ??
    store.getters["auth/authFirstName"]
  );
});
const lastName = computed(() => {
  return (
    loadedProfile.value?.last_name?.value ?? store.getters["auth/authLastName"]
  );
});

const fullname = computed(() => {
  return firstName.value + " " + lastName.value;
});

const getProfilePayload = computed(
  (): KCMPPProfile => ({
    first_name: {
      value: firstName.value,
      field_type: KCMPPFieldType.STRING,
      SyncTo: "",
      fontSize: 0,
      table: KCMPPValuesTable.EMPTY,
    },
    last_name: {
      value: lastName.value,
      field_type: KCMPPFieldType.STRING,
      SyncTo: "",
      fontSize: 0,
      table: KCMPPValuesTable.EMPTY,
    },
    full_name: {
      value: loadedProfile.value?.full_name?.value ?? fullname.value,
      field_type: KCMPPFieldType.STRING,
      SyncTo: "",
      fontSize: 0,
      table: KCMPPValuesTable.EMPTY,
    },
    photo: {
      value: photo.value,
      fontSize: 0,
      field_type: KCMPPFieldType.STRING,
      table: KCMPPValuesTable.EMPTY,
      SyncTo: "",
    },
    logo: {
      value: logo.value,
      fontSize: 0,
      field_type: KCMPPFieldType.STRING,
      table: KCMPPValuesTable.EMPTY,
      SyncTo: "",
    },
    primary_color: {
      value: brandColor.value,
      fontSize: 0,
      field_type: KCMPPFieldType.STRING,
      table: KCMPPValuesTable.EMPTY,
      SyncTo: "",
    },
    company: {
      value: company.value,
      fontSize: 0,
      field_type: KCMPPFieldType.STRING,
      table: KCMPPValuesTable.EMPTY,
      SyncTo: "",
    },
    job_title: {
      value: jobTitle.value,
      fontSize: 0,
      field_type: KCMPPFieldType.STRING,
      table: KCMPPValuesTable.EMPTY,
      SyncTo: "",
    },
    primary_role: {
      value: role.value?.value ?? "",
      fontSize: 0,
      field_type: KCMPPFieldType.STRING,
      table: KCMPPValuesTable.EMPTY,
      SyncTo: "",
    },
    city: {
      value: loadedProfile.value?.city?.value ?? "",
      field_type: KCMPPFieldType.STRING,
      SyncTo: "",
      fontSize: 0,
      table: KCMPPValuesTable.EMPTY,
    },
    state: {
      value: loadedProfile.value?.state?.value ?? "",
      field_type: KCMPPFieldType.STRING,
      SyncTo: "",
      fontSize: 0,
      table: KCMPPValuesTable.EMPTY,
    },
    location: {
      value: loadedProfile.value?.location?.value ?? "",
      field_type: KCMPPFieldType.STRING,
      SyncTo: "",
      fontSize: 0,
      table: KCMPPValuesTable.EMPTY,
    },
  })
);

const instance = getCurrentInstance();
const proxy = instance?.proxy;
const closeModal = () => {
  proxy?.$bvModal.hide("onboarding-profile-modal");
};

async function saveProfile(): Promise<void> {
  if (!role.value) {
    missingRole.value = true;
    return;
  }

  saving.value = true;
  saveError.value = false;

  const resp = await setPersonalization(getProfilePayload.value);

  if (!resp) {
    saving.value = false;
    saveError.value = true;
    return;
  }

  saving.value = false;

  proxy!.$root.$emit("save");
  closeModal();
}

return { __sfc: true,emit, activeImg, loadedProfile, photo, logo, logoPalette, company, jobTitle, brandColor, role, setUploader, imageUploaded, contrastColor, badColorClass, saving, saveError, missingRole, firstName, lastName, fullname, getProfilePayload, instance, proxy, closeModal, saveProfile, roleOptions, PhotoUpload, Multiselect, personalizationOnboardingHeader }
}

})